import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import '../common/rtCommon.css';
import tenantStorage from '../../utils/tenantStorage';
import './ViewStaffDetails.css';
import RTPageLayout from '../common/RTPageLayout';

const ViewStaffDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { staffId } = useParams();

  const [staff, setStaff] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    if (staffId) {
      fetchStaffDetails();
    }
  }, [staffId]);

  const fetchStaffDetails = async () => {
    try {
      setIsLoading(true);
      const token = tenantStorage.getItem('token');
      const response = await apiGet(`/staff/${staffId}`, {
        Authorization: `Bearer ${token}`,
      });

      if (response.status === 200) {
        setStaff(response.data);
      } else {
        toastError('Error fetching staff details');
      }
    } catch (err) {
      toastError(
        'Error fetching staff details: ' +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/staffs/edit/${staffId}`, {
      state: {
        mode: 'edit',
        initialData: staff,
        staffId: staffId,
      },
    });
  };

  const handleStatusUpdate = async () => {
    if (!staff) return;

    const newStatus = staff.status === 'Active' ? 'In Active' : 'Active';
    const confirmMessage = `Are you sure you want to mark this staff as ${newStatus}?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setIsUpdatingStatus(true);
      const token = tenantStorage.getItem('token');
      const response = await apiPost(
        `/staff/update-status/${staffId}?status=${encodeURIComponent(newStatus)}`,
        {},
        { Authorization: `Bearer ${token}` },
      );

      if (response.status === 200) {
        setStaff({ ...staff, status: newStatus });
        toastSuccess(`Staff status updated to ${newStatus} successfully`);
      } else {
        toastError('Error updating staff status');
      }
    } catch (err) {
      toastError(
        'Error updating staff status: ' +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  const formatSalary = (salary) => {
    if (!salary) return '-';
    return `₹${salary.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="Staff Details"
        mainSection={true}
      >
        <div className="loading-container">
          <RTSpinner
            size="large"
            showMessage={true}
            message="Loading staff details..."
          />
        </div>
      </RTPageLayout>
    );
  }

  if (!staff) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="Staff Details"
        actions={
          <>
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              &#8592; Back
            </RTButton>
          </>
        }
        mainSection={true}
      >
        <div className="no-data-container">
          <p>Staff not found.</p>
        </div>
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={
        <>
          Staff Details - {staff.name}
          <span
            className={`status-pill ${staff.status === 'Active' ? 'status-active' : 'status-inactive'}`}
          >
            {staff.status}
          </span>
        </>
      }
      actions={
        <>
          <RTButton
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            &#8592; Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <div className="staff-details-container">
        <div className="details-sections">
          <div className="rt-section">
            <h3 className="rt-section-title">Personal Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Name:</label>
                <span>{staff.name}</span>
              </div>
              <div className="detail-item">
                <label>Date of Birth:</label>
                <span>{formatDate(staff.dob)}</span>
              </div>
              <div className="detail-item">
                <label>Mobile Number:</label>
                <span>{staff.mobile}</span>
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span>{staff.email || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Aadhar Number:</label>
                <span>{staff.aadhar || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Address:</label>
                <span>{staff.address}</span>
              </div>
              <div className="detail-item">
                <label>Residential Address:</label>
                <span>{staff.residential_address}</span>
              </div>
            </div>
          </div>

          <div className="rt-section">
            <h3 className="rt-section-title">Work Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Job Type:</label>
                <span>{staff.job_type}</span>
              </div>
              <div className="detail-item">
                <label>Monthly Salary:</label>
                <span>{formatSalary(staff.monthly_salary)}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          className="edit-button-container"
          style={{
            textAlign: 'center',
            marginTop: '30px',
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
          }}
        >
          <RTButton
            className="edit-btn"
            variant="primary"
            permissionKey="staffs.add"
            onClick={handleEdit}
          >
            Edit Staff
          </RTButton>
          <RTButton
            className="status-btn"
            variant={staff.status === 'Active' ? 'danger' : 'success'}
            permissionKey="staffs.add"
            onClick={handleStatusUpdate}
            disabled={isUpdatingStatus}
          >
            {isUpdatingStatus
              ? 'Updating...'
              : staff.status === 'Active'
                ? 'Mark In-Active'
                : 'Mark Active'}
          </RTButton>
        </div>
      </div>
    </RTPageLayout>
  );
};

export default ViewStaffDetails;
